import { useState } from 'react'
import { Button } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { PlusIcon } from '@/components/ui/icons'
import { translated } from '@/lib/api/common'

import { useOptionSetActions, useOptionSets } from '../hooks'
import type { OptionSet } from '../types'
import { OptionSetFormDialog } from './option-set-form-dialog'
import { OptionSetsTable } from './option-sets-table'
import type { OptionSetRowAction } from './option-sets-table'

/**
 * The axes a product's variants are built from.
 *
 * Next to Choice Groups rather than merged with them: an option set is
 * something the PRODUCT varies along and the customer never picks directly,
 * while a choice group is exactly what they do pick. One screen would have to
 * explain that difference in every row.
 */
export function OptionSetsPanel() {
  const { optionSets, isLoading, isRefreshing, error, retry } = useOptionSets()
  const { remove } = useOptionSetActions()

  const [editing, setEditing] = useState<OptionSet | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<OptionSet | null>(null)

  function openForm(optionSet: OptionSet | null) {
    setEditing(optionSet)
    setIsFormOpen(true)
  }

  function handleRowAction(action: OptionSetRowAction, optionSet: OptionSet) {
    if (action === 'edit') return openForm(optionSet)
    setPendingDelete(optionSet)
  }

  return (
    <>
      <ModuleIntro
        description="The axes your product variants are built from — Size, Colour. Define one once and reuse it across the catalogue."
        action={
          <Button
            variant="primary"
            size="sm"
            className="h-9 rounded-full px-4 font-medium"
            onPress={() => openForm(null)}
          >
            <PlusIcon width={16} height={16} />
            New option
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your product options" error={error} onRetry={retry} />
      ) : optionSets.length === 0 ? (
        <EmptyState
          title="No product options yet."
          description="Define an axis once — Size, Colour — and reuse it across products that vary along it."
          action={{ label: 'New option', onPress: () => openForm(null) }}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <OptionSetsTable
              optionSets={optionSets}
              onAction={handleRowAction}
              busyIds={remove.isPending && remove.variables ? [remove.variables] : []}
            />
          </div>

          <p className="text-muted text-xs tabular-nums">
            {optionSets.length} {optionSets.length === 1 ? 'option' : 'options'}
          </p>
        </>
      )}

      <OptionSetFormDialog
        key={editing?.id ?? 'new'}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        optionSet={editing}
        onSaved={() => setIsFormOpen(false)}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        onOpenChange={(isOpen) => !isOpen && setPendingDelete(null)}
        title="Delete this option?"
        confirmLabel="Delete"
        isBusy={remove.isPending}
        error={remove.error}
        errorFallback="Could not delete this option."
        onConfirm={async () => {
          if (!pendingDelete) return
          await remove.mutateAsync(pendingDelete.id)
          setPendingDelete(null)
        }}
        body={
          <>
            <span className="text-foreground font-medium">
              {pendingDelete ? translated(pendingDelete.name) : ''}
            </span>{' '}
            and its values will be removed. Variants already built from it keep their own
            attributes.
          </>
        }
      />
    </>
  )
}
